/**
 * TaskItemCard — one task item in the Task Group modal, as corp's card:
 *   - the header (always shown, a click opens / closes it): the task's type
 *     badge and name, what is wrong with it ("No task selected", "No user",
 *     "No group"), "Won't send" for an automated task not assigned to
 *     Automatic, the modified pill, when it is due, recurring, who does it,
 *     the priority dot and remove;
 *   - the body: Task (with an edit button for the task itself), Priority,
 *     Date to Perform (amount, unit, after / before, which date; a date for
 *     "Other Date"), Recurring (repeat N times, every N units) and Assigned
 *     To (a user group — one member or all of them — or a user).
 * Corp's compact sizes: the controls are h-8 / text-xs at corp's widths.
 * Inside a locked FieldLockScope (a Locked Admin page) it only reads: no
 * remove, the radios don't change, the task opens from a View icon, and the
 * problem and "won't send" markers are left out (nothing can fix them there).
 */
import type { ReactNode } from 'react';
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Edit,
  Eye,
  Mail,
  MessageSquare,
  RefreshCw,
  Tag,
  Trash2,
  User,
  Users,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/lib/utils';
import {
  DATE_TYPE_LABELS,
  DATE_TYPE_ORDER,
  PRIORITY_DOT,
  PRIORITY_LABELS,
  TIME_TYPE_LABELS,
  type AccountUser,
  type FilterOption,
  type TaskItem,
} from '@/services/taskGroupsAdmin.service';
import { TASK_TYPE_BADGE, UNKNOWN_TYPE_BADGE, isAutomatedTaskType, taskTypeLabel, type AdminTask } from '@/services/tasksAdmin.service';
import { DialogField, LABEL_CLASS, ModifiedPill, fieldClasses } from './DialogChrome';

/** A task item in the form, with a key that stays put before it is saved. */
export type FormItem = TaskItem & { key: string };

export interface ItemProblems {
  missingTask: boolean;
  missingUser: boolean;
  missingGroup: boolean;
}

export function isGroupAssignee(assignedToID: string): boolean {
  return assignedToID.startsWith('role_') || assignedToID.startsWith('group_');
}

export function itemProblems(item: TaskItem): ItemProblems {
  const a = item.assignedToID || 'coord';
  return {
    missingTask: !item.taskID,
    missingUser: a === 'user' && !item.sendToID,
    missingGroup: isGroupAssignee(a) && a.replace(/^(role_|group_)/, '') === '',
  };
}

/** The item's task type: from the account's task, else from what the API sent with the item. */
export function itemTaskType(item: TaskItem, tasks: AdminTask[]): number {
  return tasks.find((t) => t.taskID === item.taskID)?.autoTaskID ?? item.taskAutoID;
}

/** An automated task (E-Mail, SMS, tag, survey, stage) is only sent when assigned to Automatic. */
export function wontSend(item: TaskItem, tasks: AdminTask[]): boolean {
  return item.taskID > 0 && isAutomatedTaskType(itemTaskType(item, tasks)) && (item.assignedToID || 'coord') !== 'ftp';
}

export function dueLabel(item: TaskItem): string {
  const amount = Math.abs(item.timeAmount);
  const unit = TIME_TYPE_LABELS[item.timeType] ?? 'Days';
  const when = item.timeAmount < 0 ? 'before' : 'after';
  return `${amount} ${unit} ${when} ${DATE_TYPE_LABELS[item.dateType] ?? 'Assignment Date'}`;
}

function TypeIcon({ type }: { type: number }) {
  const cls = 'h-3.5 w-3.5';
  if (type === 1 || type === 3 || type === 7) return <Mail className={cls} aria-hidden />;
  if (type === 2 || type === 4 || type === 9) return <MessageSquare className={cls} aria-hidden />;
  if (type === 6) return <Tag className={cls} aria-hidden />;
  if (type === 8 || type === 82) return <ArrowRightLeft className={cls} aria-hidden />;
  return <ClipboardCheck className={cls} aria-hidden />;
}

/** Corp's item border colour per task type. */
const ITEM_BORDER: Record<number, string> = {
  0: 'border-slate-300',
  1: 'border-blue-300',
  2: 'border-emerald-300',
  3: 'border-orange-300',
  4: 'border-amber-300',
  5: 'border-red-300',
  6: 'border-violet-300',
  7: 'border-cyan-300',
  8: 'border-fuchsia-300',
  9: 'border-teal-300',
  82: 'border-fuchsia-300',
};

/** A Priority option: corp's coloured dot and the level. */
function PriorityOption({ priority }: { priority: number }) {
  return (
    <span className="flex items-center gap-2">
      <span className={cn('h-2 w-2 shrink-0 rounded-full', PRIORITY_DOT[priority] ?? PRIORITY_DOT[2])} aria-hidden />
      {PRIORITY_LABELS[priority] ?? 'Medium'}
    </span>
  );
}

const UNIT_OPTIONS = Object.entries(TIME_TYPE_LABELS).map(([v, label]) => ({ value: Number(v), label }));

export function TaskItemCard({
  item,
  saved,
  index,
  tasks,
  users,
  roles,
  assignedToOptions,
  open,
  onToggle,
  onChange,
  onRemove,
  onRevert,
  onEditTask,
}: {
  item: FormItem;
  /** The item as loaded (none for a new item). */
  saved?: TaskItem;
  index: number;
  tasks: AdminTask[];
  users: AccountUser[];
  roles: FilterOption[];
  assignedToOptions: { value: string; label: string }[];
  open: boolean;
  onToggle: () => void;
  onChange: (next: FormItem) => void;
  onRemove: () => void;
  onRevert: () => void;
  onEditTask?: (taskID: number) => void;
}) {
  const locked = useFieldLocked();
  const id = `task-item-${item.key}`;
  const type = itemTaskType(item, tasks);
  const task = tasks.find((t) => t.taskID === item.taskID);
  const name = task?.name || item.taskName || 'Untitled';
  const problems: ItemProblems = locked ? { missingTask: false, missingUser: false, missingGroup: false } : itemProblems(item);
  const hasError = problems.missingTask || problems.missingUser || problems.missingGroup;
  const stranded = !locked && wontSend(item, tasks);
  const notSent = !hasError && stranded;
  const assigned = item.assignedToID || 'coord';
  const groupAssignee = isGroupAssignee(assigned);
  const category = groupAssignee ? 'role' : assigned;
  const roleID = groupAssignee ? assigned.replace(/^(role_|group_)/, '') : '';
  const allMembers = assigned.startsWith('role_');

  const changed = (...fields: (keyof TaskItem)[]) => !!saved && fields.some((f) => String(item[f]) !== String(saved[f]));
  const pill = (label: string, fields: (keyof TaskItem)[]): ReactNode =>
    changed(...fields) ? (
      <ModifiedPill
        label={label}
        onRevert={() => onChange({ ...item, ...Object.fromEntries(fields.map((f) => [f, saved![f]])) } as FormItem)}
      />
    ) : null;
  const anyChanged = changed(
    'taskID',
    'timeType',
    'timeAmount',
    'recurringType',
    'recurringAmount',
    'dateType',
    'assignedToID',
    'priority',
    'recurring',
    'recurringNumber',
    'customDate',
    'sendToID',
  );
  const set = (next: Partial<TaskItem>) => onChange({ ...item, ...next });

  const taskChoices = tasks
    .filter((t) => t.active || t.taskID === item.taskID)
    .map((t) => ({ value: t.taskID, label: t.active ? t.name : `${t.name} (deleted)` }));
  if (item.taskID > 0 && !taskChoices.some((c) => c.value === item.taskID)) {
    taskChoices.push({ value: item.taskID, label: `${item.taskName || `Task #${item.taskID}`} (unavailable)` });
  }

  const assignIcon =
    category === 'ftp' ? (
      <Zap className="h-3.5 w-3.5 text-amber-600" aria-label="Automatic" />
    ) : category === 'user' ? (
      <User className="h-3.5 w-3.5 text-blue-600" aria-label="User" />
    ) : category === 'role' ? (
      <Users className="h-3.5 w-3.5 text-violet-600" aria-label="User Group" />
    ) : (
      <User className="h-3.5 w-3.5 text-slate-500" aria-label={assignedToOptions.find((o) => o.value === category)?.label ?? 'Assigned'} />
    );

  return (
    <div
      data-testid="task-item"
      className={cn('overflow-hidden rounded-md border-2 bg-white', hasError ? `${fieldClasses.input('error')} bg-red-50/30` : (ITEM_BORDER[type] ?? ITEM_BORDER[0]))}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${open ? 'Close' : 'Open'} task item ${index + 1}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="flex cursor-pointer select-none items-center gap-2 bg-slate-50 px-3 py-2 transition-colors hover:bg-slate-100"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        <span className={cn('inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium', TASK_TYPE_BADGE[type] ?? UNKNOWN_TYPE_BADGE)}>
          <TypeIcon type={type} />
          {taskTypeLabel(type)}
        </span>
        <span className={cn('min-w-0 truncate text-xs font-medium', hasError ? 'text-red-700' : 'text-slate-800')} title={name}>
          {problems.missingTask ? '⚠ No task selected' : name}
        </span>
        {hasError && !problems.missingTask && (
          <span className="shrink-0 whitespace-nowrap text-[10px] font-medium text-red-500">{problems.missingUser ? '⚠ No user' : '⚠ No group'}</span>
        )}
        {notSent && (
          <span
            className="shrink-0 whitespace-nowrap text-[10px] font-medium text-amber-700"
            title={`${taskTypeLabel(type)} tasks are only sent when Assigned To is "Automatic". This task will never be sent.`}
          >
            ⚠ Won&apos;t send
          </span>
        )}
        {anyChanged && <ModifiedPill label={`task item ${index + 1}`} onRevert={onRevert} />}
        <span className="flex-1" />
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[10px] text-slate-500" title={dueLabel(item)}>
          {dueLabel(item)}
          {item.recurring && <RefreshCw className="h-3 w-3 text-blue-500" aria-label="Recurring" />}
        </span>
        <span className="shrink-0">{assignIcon}</span>
        <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', PRIORITY_DOT[item.priority] ?? PRIORITY_DOT[2])} title={PRIORITY_LABELS[item.priority] ?? 'Medium'} />
        {!locked && (
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            aria-label={`Remove task item ${index + 1}`}
            title="Remove item"
            className="h-6 w-6 text-slate-400 hover:bg-red-50 hover:text-red-600"
            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        )}
      </div>

      {open && (
        <div className="space-y-3 border-t border-slate-200 p-3">
          <div className="grid gap-3 md:grid-cols-[1fr_140px]">
            <DialogField
              label="Task"
              htmlFor={`${id}-task`}
              required
              state={problems.missingTask ? 'error' : 'none'}
              labelHint={problems.missingTask ? '— required' : undefined}
              badge={pill('Task', ['taskID'])}
              error={problems.missingTask ? 'Select a task or remove this task item' : undefined}
            >
              <div className="flex items-center gap-1">
                <SearchableCombobox<number>
                  id={`${id}-task`}
                  options={taskChoices}
                  value={item.taskID || null}
                  onChange={(v) => set({ taskID: v ?? 0 })}
                  placeholder="Select task…"
                  wrapperClassName="min-w-0 flex-1"
                />
                {item.taskID > 0 && onEditTask && (
                  <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    aria-label={locked ? 'View task' : 'Edit task'}
                    title={locked ? 'View task' : 'Edit task'}
                    leftIcon={locked ? <Eye className="h-3.5 w-3.5" /> : <Edit className="h-3.5 w-3.5" />}
                    onClick={() => onEditTask(item.taskID)}
                  />
                )}
              </div>
            </DialogField>
            <DialogField label="Priority" htmlFor={`${id}-priority`} badge={pill('Priority', ['priority'])}>
              <Select<number>
                id={`${id}-priority`}
                options={[1, 2, 3].map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
                value={item.priority || 2}
                onChange={(v) => set({ priority: v ?? 2 })}
                renderOption={(o) => <PriorityOption priority={o.value} />}
                renderValue={(o) => <PriorityOption priority={o.value} />}
                className="text-xs"
                wrapperClassName="w-fit"
              />
            </DialogField>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={LABEL_CLASS}>Date to Perform</span>
              {pill('Date to Perform', ['timeAmount', 'timeType', 'dateType', 'customDate'])}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Input
                type="number"
                min={0}
                max={364}
                aria-label="How many"
                value={Math.abs(item.timeAmount)}
                onChange={(e) => {
                  const n = Math.min(364, Math.abs(Math.trunc(Number(e.target.value) || 0)));
                  set({ timeAmount: item.timeAmount < 0 ? -n : n });
                }}
                className="h-8 py-1 text-xs"
                wrapperClassName="w-16"
              />
              <label htmlFor={`${id}-unit`} className="sr-only">
                Unit
              </label>
              <Select<number> id={`${id}-unit`} options={UNIT_OPTIONS} value={item.timeType || 1} onChange={(v) => set({ timeType: v ?? 1 })} className="h-8 py-1 text-xs" wrapperClassName="w-24" />
              <label htmlFor={`${id}-when`} className="sr-only">
                Before or after
              </label>
              <Select<string>
                id={`${id}-when`}
                options={[
                  { value: 'after', label: 'After' },
                  { value: 'before', label: 'Before' },
                ]}
                value={item.timeAmount < 0 ? 'before' : 'after'}
                onChange={(v) => {
                  const n = Math.abs(item.timeAmount);
                  set({ timeAmount: v === 'before' ? -n : n });
                }}
                className="h-8 py-1 text-xs"
                wrapperClassName="w-24"
              />
              <label htmlFor={`${id}-date`} className="sr-only">
                Date
              </label>
              <Select<number>
                id={`${id}-date`}
                options={DATE_TYPE_ORDER.map((d) => ({ value: d, label: DATE_TYPE_LABELS[d] }))}
                value={item.dateType || 1}
                onChange={(v) => set({ dateType: v ?? 1 })}
                className="h-8 py-1 text-xs"
                wrapperClassName="w-36"
              />
              {item.dateType === 2 && (
                <div className="w-36">
                  <DatePicker id={`${id}-custom-date`} value={item.customDate} onChange={(v) => set({ customDate: v })} />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Switch id={`${id}-recurring`} checked={item.recurring} onChange={(v) => set({ recurring: v })} aria-label="Recurring" />
            <label htmlFor={`${id}-recurring`} className={cn(LABEL_CLASS, locked ? 'cursor-default' : 'cursor-pointer')}>
              Recurring
            </label>
            {pill('Recurring', ['recurring', 'recurringNumber', 'recurringAmount', 'recurringType'])}
            {item.recurring && (
              <>
                <span className="text-xs text-slate-400">|</span>
                <span className="text-xs text-slate-500">Repeat</span>
                <Input
                  type="number"
                  min={2}
                  max={100}
                  aria-label="Repeat how many times"
                  value={item.recurringNumber}
                  onChange={(e) => set({ recurringNumber: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })}
                  className="h-8 py-1 text-xs"
                  wrapperClassName="w-16"
                />
                <span className="text-xs text-slate-500">times, every</span>
                <Input
                  type="number"
                  min={1}
                  max={364}
                  aria-label="Repeat every"
                  value={item.recurringAmount}
                  onChange={(e) => set({ recurringAmount: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })}
                  className="h-8 py-1 text-xs"
                  wrapperClassName="w-16"
                />
                <label htmlFor={`${id}-recurring-unit`} className="sr-only">
                  Repeat unit
                </label>
                <Select<number>
                  id={`${id}-recurring-unit`}
                  options={UNIT_OPTIONS}
                  value={item.recurringType || 1}
                  onChange={(v) => set({ recurringType: v ?? 1 })}
                  className="h-8 py-1 text-xs"
                  wrapperClassName="w-24"
                />
              </>
            )}
          </div>

          <div className={cn('flex flex-wrap items-end gap-3', (problems.missingUser || problems.missingGroup) && 'rounded-md border border-red-300 p-2')}>
            <DialogField
              label="Assigned To"
              htmlFor={`${id}-assigned`}
              state={problems.missingUser || problems.missingGroup ? 'error' : 'none'}
              labelHint={problems.missingUser || problems.missingGroup ? '— incomplete' : undefined}
              badge={pill('Assigned To', ['assignedToID', 'sendToID'])}
              className="w-56"
            >
              <Select<string>
                id={`${id}-assigned`}
                options={assignedToOptions}
                value={category}
                onChange={(v) => {
                  if (!v || v === category) return;
                  if (v === 'role') set({ assignedToID: 'group_' });
                  // A user is picked next; what sendToID held was the task's recipient, not a user.
                  else if (v === 'user') set({ assignedToID: 'user', sendToID: 0 });
                  else set({ assignedToID: v });
                }}
                className="h-8 py-1 text-xs"
                wrapperClassName="w-fit"
              />
            </DialogField>

            {groupAssignee && (
              <>
                <DialogField
                  label="Group"
                  htmlFor={`${id}-group`}
                  state={problems.missingGroup ? 'error' : 'none'}
                  error={problems.missingGroup ? 'Select a group or change the assignment' : undefined}
                  className="w-52"
                >
                  <SearchableCombobox<string>
                    id={`${id}-group`}
                    options={roles.map((r) => ({ value: r.id, label: r.name }))}
                    value={roleID || null}
                    onChange={(v) => set({ assignedToID: `${allMembers ? 'role_' : 'group_'}${v ?? ''}` })}
                    placeholder="Select group…"
                  />
                </DialogField>
                <fieldset className="space-y-1.5 pb-0.5">
                  <legend className="sr-only">Who performs it</legend>
                  {[
                    { value: 'group_', label: 'One Group Member', hint: 'Only one member of the group needs to perform this task' },
                    { value: 'role_', label: 'All Group Members', hint: 'Each member of the group must perform this task' },
                  ].map((o) => (
                    <label key={o.value} className={cn('flex items-start gap-2', locked ? 'cursor-default' : 'cursor-pointer')}>
                      {/* Locked: no onChange, so React keeps the saved choice checked. */}
                      <input
                        type="radio"
                        name={`${id}-members`}
                        className="mt-0.5"
                        checked={(o.value === 'role_') === allMembers}
                        readOnly={locked}
                        onChange={locked ? undefined : () => set({ assignedToID: `${o.value}${roleID}` })}
                      />
                      <span>
                        <span className="block text-xs font-medium text-slate-900">{o.label}</span>
                        <span className="block text-[10px] leading-tight text-slate-500">{o.hint}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
              </>
            )}

            {category === 'user' && (
              <DialogField
                label="User"
                htmlFor={`${id}-user`}
                state={problems.missingUser ? 'error' : 'none'}
                error={problems.missingUser ? 'Select a user or change the assignment' : undefined}
                className="min-w-[200px] flex-1"
              >
                <SearchableCombobox<number>
                  id={`${id}-user`}
                  options={users.map((u) => ({ value: u.userID, label: u.name }))}
                  value={item.sendToID || null}
                  onChange={(v) => set({ sendToID: v ?? 0 })}
                  placeholder="Select user…"
                />
              </DialogField>
            )}

            {category === 'ftp' && (
              <p className="max-w-xs self-center text-xs leading-snug text-slate-500">
                {isAutomatedTaskType(type)
                  ? `This ${taskTypeLabel(type)} task will be sent automatically when the trigger fires.`
                  : 'This task will be completed automatically when the trigger fires.'}
              </p>
            )}
            {stranded && (
              <p className="max-w-xs self-center text-xs leading-snug text-amber-700">
                This is a <strong>{taskTypeLabel(type)}</strong> task, which is only sent when Assigned To is &ldquo;Automatic&rdquo;. Assigned to a person it will
                never be sent — it will just appear as a due task.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
